<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.trainings.createTitle') }}</h2>
        <button type="button" class="btn-close" @click="$emit('close')">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-banner">{{ error }}</div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelTitle') }} <span class="required">*</span></label>
          <input v-model="form.title" type="text" class="input" maxlength="255" :placeholder="$t('bo.trainings.titlePlaceholder')" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.common.description') }} <span class="required">*</span></label>
          <textarea v-model="form.description" class="textarea" rows="3" maxlength="2000" :placeholder="$t('bo.trainings.descriptionPlaceholder')" />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelCredits') }} <span class="required">*</span></label>
            <input v-model.number="form.credit_cost" type="number" class="input" min="0" step="0.01" />
          </div>
          <div class="form-group">
            <label>{{ $t('bo.trainings.colLevel') }}</label>
            <select v-model="form.level" class="input">
              <option value="">{{ $t('bo.trainings.chooseLevel') }}</option>
              <option value="beginner">{{ $t('bo.trainings.levelBeginner') }}</option>
              <option value="intermediate">{{ $t('bo.trainings.levelIntermediate') }}</option>
              <option value="advanced">{{ $t('bo.trainings.levelAdvanced') }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelCategory') }}</label>
            <input v-model="form.category" type="text" class="input" maxlength="100" />
          </div>
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelProvider') }}</label>
            <input v-model="form.provider" type="text" class="input" maxlength="255" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelDuration') }}</label>
            <input v-model.number="form.duration_minutes" type="number" class="input" min="0" />
          </div>
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelType') }}</label>
            <select v-model="form.training_type" class="input">
              <option value="">{{ $t('bo.trainings.chooseType') }}</option>
              <option value="online">{{ $t('bo.trainings.typeOnline') }}</option>
              <option value="in_person">{{ $t('bo.trainings.typeInPerson') }}</option>
              <option value="hybrid">{{ $t('bo.trainings.typeHybrid') }}</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelUrl') }}</label>
          <input v-model="form.url" type="url" class="input" maxlength="500" placeholder="https://..." />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelThumbnail') }}</label>
          <CoverImageInput v-model="form.thumbnail_url" v-model:media-id="form.thumbnail_id" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelLanguages') }}</label>
          <input v-model="form.languagesText" type="text" class="input" :placeholder="$t('bo.trainings.languagesPlaceholder')" />
        </div>

        <label class="checkbox-row">
          <input v-model="form.is_active" type="checkbox" />
          {{ $t('bo.trainings.labelActive') }}
        </label>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn-cancel" @click="$emit('close')">{{ $t('bo.common.cancel') }}</button>
        <button type="button" class="btn-submit" :disabled="loading || !canSubmit" @click="submit">
          {{ loading ? $t('bo.common.creating') : $t('bo.trainings.createTraining') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import trainingAdminService from '@/services/trainingAdminService'
import CoverImageInput from '@/components/ui/CoverImageInput.vue'

const { t } = useI18n()
const emit = defineEmits(['close', 'created'])

const loading = ref(false)
const error = ref('')
const form = ref({
  title: '',
  description: '',
  credit_cost: 0,
  level: '',
  category: '',
  provider: '',
  duration_minutes: null,
  training_type: '',
  url: '',
  thumbnail_url: '',
  thumbnail_id: null,
  languagesText: 'en',
  is_active: true,
})

const canSubmit = computed(
  () =>
    form.value.title.trim() &&
    form.value.description.trim() &&
    form.value.credit_cost !== null &&
    form.value.credit_cost !== '' &&
    Number(form.value.credit_cost) >= 0
)

const buildPayload = () => {
  const languages = form.value.languagesText
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean)

  const payload = {
    title: form.value.title.trim(),
    description: form.value.description.trim(),
    credit_cost: Number(form.value.credit_cost),
    is_active: !!form.value.is_active,
  }

  if (form.value.level) payload.level = form.value.level
  if (form.value.category.trim()) payload.category = form.value.category.trim()
  if (form.value.provider.trim()) payload.provider = form.value.provider.trim()
  if (form.value.training_type.trim()) payload.training_type = form.value.training_type.trim()
  if (form.value.url.trim()) payload.url = form.value.url.trim()
  const thumbnailUrl = form.value.thumbnail_url.trim()
  if (thumbnailUrl) {
    payload.thumbnail_url = thumbnailUrl
    payload.thumbnail_id = form.value.thumbnail_id ? Number(form.value.thumbnail_id) : null
  } else {
    payload.thumbnail_url = null
    payload.thumbnail_id = null
  }
  if (form.value.duration_minutes !== null && form.value.duration_minutes !== '') {
    payload.duration_minutes = Number(form.value.duration_minutes)
  }
  if (languages.length) payload.languages = languages

  return payload
}

const submit = async () => {
  if (!canSubmit.value) return
  loading.value = true
  error.value = ''
  try {
    await trainingAdminService.create(buildPayload())
    emit('created')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.trainings.failedCreate')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.required {
  color: #dc2626;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.input,
.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
  background: white;
  font-family: inherit;
}

.textarea {
  resize: vertical;
}

.input:focus,
.textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel,
.btn-submit {
  padding: 0.5rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.btn-submit {
  background: #3b82f6;
  border: none;
  color: white;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
